import { useCallback, useEffect, useMemo, useState } from "react";
import { MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import BorrowLoanDialog from "../components/BorrowLoanDialog";
import CustomerTable from "../components/CustomerTable";
import SalesScopeSelector from "../components/SalesScopeSelector";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";

const emptyForm = (agentId = "") => ({
  full_name: "",
  ic_passport: "",
  occupation: "",
  address: "",
  agent_id: agentId,
});

export default function CustomerRegisterPageV2({ user }) {
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [scopeId, setScopeId] = useState("");
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [borrowing, setBorrowing] = useState(null);
  const [form, setForm] = useState(() => emptyForm(user.id));
  const canAssignAgent = ["admin", "master"].includes(user.role);

  const loadCustomers = useCallback(async () => {
    try {
      const customerUrl = `/customers${scopeId ? `?owner_id=${scopeId}` : ""}`;
      const requests = [api.get(customerUrl)];
      if (canAssignAgent) {
        requests.push(api.get("/sales-scopes"));
      }
      const [customerResponse, scopeResponse] = await Promise.all(requests);
      setCustomers(customerResponse.data);
      setAgents(scopeResponse?.data || []);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  }, [canAssignAgent, scopeId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const filteredCustomers = useMemo(() => {
    const normalizedQuery = query.toLowerCase();
    return customers.filter((customer) => {
      return Object.values(customer).some((value) => {
        return String(value).toLowerCase().includes(normalizedQuery);
      });
    });
  }, [customers, query]);

  const updateForm = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);

  const openCreate = () => {
    setForm(emptyForm(canAssignAgent ? user.id : ""));
    setEditing({ mode: "create" });
  };

  const openEdit = (customer) => {
    setForm({
      full_name: customer.full_name,
      ic_passport: customer.ic_passport,
      occupation: customer.occupation,
      address: customer.address,
      agent_id: customer.owner_id,
    });
    setEditing({ mode: "edit", customer });
  };

  const saveCustomer = async (event) => {
    event.preventDefault();
    try {
      if (editing.mode === "create") {
        await api.post("/customers", form);
      } else {
        const { agent_id, ...customerFields } = form;
        await api.put(`/customers/${editing.customer.id}`, customerFields);
      }
      toast.success(editing.mode === "create" ? "Customer added" : "Customer details updated");
      setEditing(null);
      loadCustomers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const deleteCustomer = async () => {
    try {
      await api.delete(`/customers/${deleting.id}`);
      toast.success("Customer profile deleted");
      setDeleting(null);
      loadCustomers();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <div className="page customers-page" data-testid="customers-page">
      <header className="page-header">
        <div>
          <h1>Customers</h1>
          <p data-testid="customers-count-label">
            {customers.length} customer{customers.length === 1 ? "" : "s"}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-customer-button">
          <Plus size={16} /> Add Customer
        </Button>
      </header>
      {canAssignAgent && (
        <SalesScopeSelector
          value={scopeId}
          onChange={setScopeId}
          testId="customer-agent-filter-select"
        />
      )}
      <div className="customer-search">
        <MagnifyingGlass size={16} />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search by name, IC / passport, occupation or address"
          data-testid="customer-search-input"
        />
      </div>
      <CustomerTable
        customers={filteredCustomers}
        isAdmin={canAssignAgent}
        onBorrow={setBorrowing}
        onEdit={openEdit}
        onDelete={setDeleting}
      />
      <BorrowLoanDialog
        customer={borrowing}
        onClose={() => setBorrowing(null)}
        onDone={loadCustomers}
      />
      <Dialog open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="customer-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.mode === "create" ? "Add Customer" : "Edit Customer"}</DialogTitle>
            <DialogDescription>
              Create or update the borrower profile used for loan records.
            </DialogDescription>
          </DialogHeader>
          <form className="dialog-form two-column" onSubmit={saveCustomer}>
            <div>
              <Label htmlFor="customer-name">Full Name</Label>
              <Input
                id="customer-name"
                data-testid="customer-name-input"
                value={form.full_name}
                onChange={(event) => updateForm("full_name", event.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="customer-ic">IC / Passport</Label>
              <Input
                id="customer-ic"
                data-testid="customer-ic-input"
                value={form.ic_passport}
                onChange={(event) => updateForm("ic_passport", event.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="customer-occupation">Occupation</Label>
              <Input
                id="customer-occupation"
                data-testid="customer-occupation-input"
                value={form.occupation}
                onChange={(event) => updateForm("occupation", event.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="customer-address">Address</Label>
              <Input
                id="customer-address"
                data-testid="customer-address-input"
                value={form.address}
                onChange={(event) => updateForm("address", event.target.value)}
                required
              />
            </div>
            {canAssignAgent && editing?.mode === "create" && (
              <div>
                <Label htmlFor="customer-agent">Agent</Label>
                <select
                  id="customer-agent"
                  data-testid="customer-agent-select"
                  value={form.agent_id}
                  onChange={(event) => updateForm("agent_id", event.target.value)}
                  required
                >
                  <option value="">Select agent</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}{agent.is_self ? " · Me" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Button type="submit" data-testid="save-customer-button">Save Customer</Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent data-testid="delete-customer-dialog">
          <DialogHeader>
            <DialogTitle>Delete customer profile?</DialogTitle>
            <DialogDescription>
              This removes the customer and all linked loan records from the register.
            </DialogDescription>
          </DialogHeader>
          <p className="delete-note" data-testid="delete-customer-confirmation">
            This removes {deleting?.full_name} from the customer register.
          </p>
          <div className="dialog-actions">
            <Button
              variant="outline"
              onClick={() => setDeleting(null)}
              data-testid="cancel-delete-customer-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={deleteCustomer}
              data-testid="confirm-delete-customer-button"
            >
              Delete Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}